import React, { Component } from 'react';
import PropTypes from 'prop-types';

import { connect } from 'react-redux';
import {
  signUpPasswordless,
  sendGoogleOAuthCode,
  googleOAuthRedirect,
  resetEmailError,
  setEmailInputToDanger,
  load as loadAuth,
} from 'redux/modules/auth';
import Panel from 'components/Panel/Panel';
import {
  ControlLabel,
  FormGroup,
  Form,
  Col,
  Button,
  HelpBlock,
} from 'react-bootstrap';
import { withRouter } from 'react-router';
import GoogleButton from 'components/Post/PostSelectRole/GoogleButton';
import Separator from 'components/Shared/Separator/Separator';
import Recaptcha from 'react-google-invisible-recaptcha';

import EmailInput from 'components/SignUp/EmailInput';
import AccountTypeInput from 'components/SignUp/AccountTypeInput.js';
import SvgIcon from 'components/SvgIcon/SvgIcon';
import styles from './Signup.module.scss';
import {
  AuthShell,
  OrDivider,
  GoogleContinueButton,
  authStyles,
} from 'components/Auth';
import { setLastAuth, LAST_AUTH_METHODS } from 'helpers/lastAuth';
import Loader from 'components/Loader/Loader';
import { trackEvent } from 'helpers/Analytics';
import { isNativeApp } from 'redux/modules/nativeApp';
import {
  buildSignupAttributionUtm,
  buildVisitorSegmentation,
} from 'helpers/signupAttributionUtm';
import clientBackdropDesktop from 'components/Auth/images/signup-backdrop-client.jpg';
import clientBackdropTablet from 'components/Auth/images/signup-backdrop-client-tablet.jpg';
import freelancerBackdropDesktop from 'components/Auth/images/signup-backdrop-freelancer.jpg';
import freelancerBackdropTablet from 'components/Auth/images/signup-backdrop-freelancer-tablet.jpg';
import {
  SIGNUP_BACKDROP_TEST,
  subscribeToSignupBackdropVariant,
} from 'helpers/signupBackdropExperiment';
import {
  buildSignupPathWithAccountType,
  readSignupAccountTypeFromSearch,
  resolveSignupStepAfterPopState,
} from 'helpers/signupIntentQuery';
import {
  SELLER_EMAIL_HOLD_MS,
  shouldShowSignupEmailStep,
} from 'helpers/signupSellerEmailHold';
import {
  storePreSignupAudience,
  maybeTrackAudienceTransitionOnSignup,
} from 'helpers/audienceTracking';

const CLIENT_BACKDROP = {
  desktop: clientBackdropDesktop,
  tablet: clientBackdropTablet,
};
const FREELANCER_BACKDROP = {
  desktop: freelancerBackdropDesktop,
  tablet: freelancerBackdropTablet,
};

const ROLE_OPTIONS = [
  {
    value: 'seller',
    title: "I'm looking for work",
    subline: 'Get matched to jobs and showcase your work',
  },
  {
    value: 'buyer',
    title: "I'm hiring",
    subline: 'Post a project for proposals or browse experts',
  },
];

const RoleCheckIcon = () => (
  <svg
    className={authStyles.roleCheck}
    viewBox="0 0 12 12"
    aria-hidden="true"
    focusable="false"
  >
    <path
      d="M2.5 6.2L4.8 8.5L9.5 3.5"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

@connect(
  (state) => ({
    emailError: state.auth.emailError,
    accountTypeError: state.auth.accountTypeError,
    emailValidationType: state.auth.emailValidationType,
    signedUpToGoogle: state.auth.signedUpToGoogle,
    signingUpViaGoogle: state.auth.signingUpViaGoogle,
    googleSignUpFailed: state.auth.googleSignUpFailed,
    signUpPasswordlessError: state.auth.signUpPasswordlessError,
    signUpPasswordlessLoading: state.auth.signUpPasswordlessLoading,
    isNativeApp: isNativeApp(state),
  }),
  {
    signUpPasswordless,
    loadAuth,
    setEmailInputToDanger,
    resetEmailError,
    googleOAuthRedirect,
    sendGoogleOAuthCode,
  }
)
@withRouter
class SignUp extends Component {
  static propTypes = {
    location: PropTypes.object,
    history: PropTypes.object,
    signUpPasswordless: PropTypes.func,
    loadAuth: PropTypes.func,
    emailError: PropTypes.array,
    accountTypeError: PropTypes.array,
    emailValidationType: PropTypes.string,
    setEmailInputToDanger: PropTypes.func,
    resetEmailError: PropTypes.func,
    googleOAuthRedirect: PropTypes.func,
    sendGoogleOAuthCode: PropTypes.func,
    signedUpToGoogle: PropTypes.bool,
    signingUpViaGoogle: PropTypes.bool,
    redirect: PropTypes.string,
    redirectFromGoogle: PropTypes.string,
    defaultAccountType: PropTypes.string,
    isNativeApp: PropTypes.bool,
    googleSignUpFailed: PropTypes.bool,
    signUpPasswordlessError: PropTypes.string,
    signUpPasswordlessLoading: PropTypes.bool,
    /** progressive = /signup card UI; default keeps embedded modal layout */
    layout: PropTypes.oneOf(['default', 'progressive']),
  };

  static defaultProps = {
    layout: 'default',
  };

  constructor(props) {
    super(props);
    const initialType = props.defaultAccountType || '';
    const progressiveSellerPrefill =
      props.layout === 'progressive' && initialType === 'seller';
    this.state = {
      email: '',
      type: initialType,
      errors: {},
      shouldRedirect: false,
      hasTriedToContinue: false,
      hide: false,
      showTypeError: false,
      disableSubmitButton: true,
      signingUp: false,
      toRender: null,
      isCollector: false,
      apiError: null,
      // Progressive disclosure: step 2 once a role is chosen (unless prefilled)
      step: initialType ? 2 : 1,
      // PostHog experiment: control until flags load (matches server render)
      backdropVariant: 'control',
      // TP-502: hide seller email briefly so Mida B can redirect first
      sellerEmailHold: progressiveSellerPrefill,
    };
    this._pushedStepHistory = false;
    this._sellerEmailHoldTimer = null;
  }

  componentDidMount() {
    const { location, layout } = this.props;
    trackEvent('user-lands-on-signup', { ucc: location.query.ucc || null });

    void this.handleGoogleOauthCode();
    if (location.query.email) {
      this.setState({
        email: location.query.email,
      });
    }

    if (layout === 'progressive') {
      window.addEventListener('popstate', this.handlePopState);
      this.unsubscribeBackdropVariant = subscribeToSignupBackdropVariant(
        (backdropVariant) => this.setState({ backdropVariant })
      );
      // Prefill /signup?type=seller: same hold as role-select entry
      if (this.state.sellerEmailHold) {
        this.scheduleSellerEmailHoldRelease();
      }
    }
  }

  componentWillUnmount() {
    this.clearSellerEmailHoldTimer();
    if (this.props.layout === 'progressive') {
      window.removeEventListener('popstate', this.handlePopState);
      if (this.unsubscribeBackdropVariant) this.unsubscribeBackdropVariant();
    }
  }

  clearSellerEmailHoldTimer = () => {
    if (this._sellerEmailHoldTimer) {
      clearTimeout(this._sellerEmailHoldTimer);
      this._sellerEmailHoldTimer = null;
    }
  };

  /**
   * After SELLER_EMAIL_HOLD_MS, reveal classic email (variant A / fail-open).
   * B redirects during the hold via Mida — component unmounts; timer cleared.
   */
  scheduleSellerEmailHoldRelease = () => {
    this.clearSellerEmailHoldTimer();
    this._sellerEmailHoldTimer = setTimeout(() => {
      this._sellerEmailHoldTimer = null;
      this.setState({ sellerEmailHold: false });
    }, SELLER_EMAIL_HOLD_MS);
  };

  componentDidUpdate(prevProps, prevState) {
    const {
      signedUpToGoogle,
      signUpPasswordlessError,
      signUpPasswordlessLoading,
    } = this.props;
    const { email, type } = this.state;
    const { email: prevEmail, type: prevType } = prevState;

    if (!prevProps.signedUpToGoogle && signedUpToGoogle) {
      setLastAuth({
        email: this.state.email,
        method: LAST_AUTH_METHODS.GOOGLE,
      });
      this.loginAndRedirect();
    }

    // Handle API errors from Redux
    if (
      signUpPasswordlessError !== prevProps.signUpPasswordlessError &&
      signUpPasswordlessError
    ) {
      this.setState({ apiError: signUpPasswordlessError });
    }

    // Login Input change — update submit enablement without surfacing field errors
    if (email !== prevEmail || type !== prevType) {
      this.setState({ disableSubmitButton: !this.canSubmit() });
    }

    if (signUpPasswordlessLoading !== prevProps.signUpPasswordlessLoading) {
      this.setState({ signingUp: signUpPasswordlessLoading });
    }
  }

  handlePopState = (event) => {
    const search =
      typeof window !== 'undefined' && window.location
        ? window.location.search || ''
        : '';
    const next = resolveSignupStepAfterPopState({
      historyState: event && event.state,
      currentStep: this.state.step,
      hasDefaultAccountType: !!this.props.defaultAccountType,
      urlAccountType: readSignupAccountTypeFromSearch(search),
    });
    if (!next) return;

    const patch = { step: next.step, showTypeError: false };
    if (next.type) {
      patch.type = next.type;
      patch.isCollector = false;
    }
    // Back to role: cancel seller hold. Forward restore: do not re-hold
    // (Mida already had a chance on first seller entry).
    if (next.step === 1) {
      this.clearSellerEmailHoldTimer();
      patch.sellerEmailHold = false;
    }
    this.setState(patch);
    this._pushedStepHistory = next.pushedStepHistory;

    // Back to role step: drop type= so Mida does not keep matching mid-choice.
    if (next.clearUrlType) {
      this.replaceSignupAccountTypeInUrl(null);
    }
  };

  /**
   * TP-502 Option A: put type=seller|buyer on the URL (preserve UTMs) so Mida
   * can run a split-URL test only on /signup?type=seller. No in-app redirect.
   *
   * Uses native history.replaceState (not react-router history.replace) so
   * connected-react-router / redux-async-connect do not treat this as a route
   * change — that path flashes App's green react-top-loading-bar. Mida still
   * reads the real browser URL for targeting.
   */
  replaceSignupAccountTypeInUrl = (accountType) => {
    if (typeof window === 'undefined' || !window.history || !window.location) {
      return;
    }

    const { pathname, search } = window.location;
    const next = buildSignupPathWithAccountType(pathname, search, accountType);
    try {
      window.history.replaceState(window.history.state, '', next);
    } catch (e) {
      // Ignore history failures — signup must still work
    }
  };

  saveLocalUserType = () => {
    const { type } = this.state;
    try {
      localStorage.setItem('userType', JSON.stringify({ type }));
    } catch (error) {
      console.warn('Unable to save user type locally.', error.message);
    }
  };

  saveLocalAICollectorData = () => {
    const { isCollector, shouldRedirect } = this.state;
    try {
      localStorage.setItem('isCollector', JSON.stringify({ isCollector }));
      localStorage.setItem(
        'shouldRedirect',
        JSON.stringify({ shouldRedirect })
      );
    } catch (error) {
      console.warn(
        'Unable to save isCollector/ shouldRedirect locally.',
        error.message
      );
    }
  };

  retrieveLocalUserType = () => {
    let type = 'seller';
    try {
      type = JSON.parse(localStorage.getItem('userType')).type;
    } catch (error) {
      console.warn('Unable to retrieve local user type.', error.message);
    }
    return type;
  };

  retrieveLocalIsCollector = () => {
    if (!__CLIENT__) {
      return false;
    }
    let isCollector = false;
    try {
      isCollector =
        JSON.parse(localStorage.getItem('isCollector'))?.isCollector || false;
    } catch (error) {
      console.warn('Unable to retrieve local isCollector.', error.message);
    }
    return isCollector;
  };

  retrieveLocalShouldRedirect = () => {
    if (!__CLIENT__) {
      return false;
    }
    let shouldRedirect = false;
    try {
      shouldRedirect =
        JSON.parse(localStorage.getItem('shouldRedirect'))?.shouldRedirect ||
        false;
    } catch (error) {
      console.warn('Unable to retrieve local shouldRedirect.', error.message);
    }
    return shouldRedirect;
  };

  handleGoogleOauthCode = async () => {
    const googleOauthCode = this.isGoogleOAuthCode();
    if (googleOauthCode) {
      this.setState({ toRender: 'loader' });
      const { sendGoogleOAuthCode } = this.props;
      const type = this.retrieveLocalUserType();
      const redirect = 'signup';
      const aiCollector = this.retrieveLocalIsCollector();
      const utm = await buildSignupAttributionUtm();
      // ICP / visitor segmentation is client-only (TP-240)
      const visitor = type === 'buyer' ? buildVisitorSegmentation() : null;
      sendGoogleOAuthCode(
        googleOauthCode,
        redirect,
        type,
        aiCollector,
        utm,
        visitor
      );
    } else {
      this.setState({ toRender: 'signup' });
    }
  };

  isGoogleOAuthCode() {
    const urlString = window.location.href;
    const url = new URL(urlString);
    return url.searchParams.get('code');
  }

  resetHasTriedToContinue = () => {
    this.setState({
      hasTriedToContinue: false,
    });
  };

  handleSubmit = async (event) => {
    event.preventDefault();

    const utm = await buildSignupAttributionUtm();
    // ICP / visitor segmentation is client-only (TP-240)
    const visitor =
      this.state.type === 'buyer' ? buildVisitorSegmentation() : null;

    const signUpDetails = {
      email: this.state.email,
      type: this.state.type,
      utm,
      ...(visitor ? { visitor } : {}),
      ai_collector: this.state.isCollector,
    };

    if (this.recaptcha) {
      this.setState({ signUpDetails, emailError: null }, () => {
        this.recaptcha.execute();
      });
    } else {
      this.handleValidationSignUpAndLogin(signUpDetails);
    }
  };

  handleValidationSignUpAndLogin = async (signUpDetails) => {
    trackEvent('signup-passwordless-attempt', { signUpDetails });
    if (
      this.props.emailValidationType === 'Invalid' &&
      !this.state.hasTriedToContinue
    ) {
      this.setState({
        hasTriedToContinue: true,
      });
      this.props.setEmailInputToDanger();
    } else {
      if (this.handleValidation()) {
        // Clear any previous API errors
        this.setState({ apiError: null });

        if (this.state.type === 'buyer') {
          const visitor = buildVisitorSegmentation();
          if (visitor?.audience) {
            storePreSignupAudience(visitor.audience);
          }
        }

        await this.props.signUpPasswordless(signUpDetails);

        // Check for API errors
        if (this.props.signUpPasswordlessError) {
          trackEvent('signup-passwordless-error', {
            error: this.props.signUpPasswordlessError,
          });
          this.setState({
            apiError: this.props.signUpPasswordlessError,
          });
          return;
        }

        // Only redirect if there are no validation errors and no API errors
        if (
          this.props.emailError.length === 0 &&
          this.props.accountTypeError.length === 0 &&
          !this.props.signUpPasswordlessError
        ) {
          this.redirectToAuthCode();
        }
      }
    }
  };

  handleRecaptchaResolved = async () => {
    const signUpDetails = {
      ...this.state.signUpDetails,
      recaptcha: this.recaptcha.getResponse(),
    };

    try {
      await this.handleValidationSignUpAndLogin(signUpDetails);
    } finally {
      // Reset reCAPTCHA after the submission attempt so it can be used again
      // (e.g. if validation fails and the user wants to resubmit).
      if (this.recaptcha) {
        this.recaptcha.reset();
      }

      this.setState({ signUpDetails: null });
    }
  };

  loginAndRedirect = async () => {
    const user = await this.props.loadAuth();
    if (user) {
      maybeTrackAudienceTransitionOnSignup({ user });
    }
    this.setState({ shouldRedirect: true });
  };

  redirectToAuthCode = () => {
    const { email } = this.state;
    trackEvent('signup-passwordless-success', {
      email,
    });
    // Encode email in base64
    const encodedEmail = btoa(email);
    this.props.history.push(`/auth/code?email=${encodedEmail}`);
  };

  typeValidation = () => {
    const { type } = this.state;
    const { accountTypeError, defaultAccountType } = this.props;

    if (type === '' && !defaultAccountType) {
      accountTypeError.length = 0;
      accountTypeError.push('Please select account type');
      return false;
    }

    accountTypeError.length = 0;
    return true;
  };

  emailValidation = () => {
    const { email } = this.state;
    const { emailError } = this.props;

    const handleEmailError = (errorMessage) => {
      emailError.length = 0;
      emailError.push(errorMessage);
    };

    if (email === '') {
      handleEmailError('Please enter your email address');
      return false;
    }

    if (!email.includes('@') || !email.includes('.')) {
      handleEmailError("Email doesn't contain @ or .");
      return false;
    }

    emailError.length = 0;
    return true;
  };

  handleValidation = () => {
    let result = true;

    result = this.typeValidation() && this.emailValidation();

    if (result === false) {
      this.setState(this.state);
    }
    return result;
  };

  /** Silent check for CTA enablement — does not mutate emailError. */
  canSubmit = () => {
    const { email, type } = this.state;
    const { defaultAccountType } = this.props;
    const hasType = type !== '' || !!defaultAccountType;
    const hasValidEmail =
      email !== '' && email.includes('@') && email.includes('.');
    return hasType && hasValidEmail;
  };

  handleEmailChange = (event) => {
    this.setState({
      email: event.target.value,
      hasTriedToContinue: false,
    });
    if (this.props.emailError && this.props.emailError.length > 0) {
      this.props.emailError.length = 0;
      this.props.resetEmailError();
    }
  };

  handleTypeChange = (event) => {
    event.target.value === 'collector'
      ? this.setState({ type: 'seller', isCollector: true })
      : this.setState({ type: event.target.value, isCollector: false });
  };

  handleRoleSelect = (value) => {
    if (this.props.emailError && this.props.emailError.length > 0) {
      this.props.emailError.length = 0;
    }
    if (this.props.resetEmailError) {
      this.props.resetEmailError();
    }

    const enteringStep2 = this.state.step === 1;
    // Sellers: hold email UI so Mida B can redirect before classic form paints.
    // Buyers: show email immediately (out of experiment).
    this.clearSellerEmailHoldTimer();
    this.setState({
      type: value,
      isCollector: false,
      step: 2,
      showTypeError: false,
      sellerEmailHold: value === 'seller',
    });
    if (value === 'seller') {
      this.scheduleSellerEmailHoldRelease();
    }
    trackEvent('signup-role-selected', { type: value });

    // Intent URL for Mida: seller → ?type=seller (experiment target); buyer → ?type=buyer.
    this.replaceSignupAccountTypeInUrl(value);

    if (enteringStep2 && !this._pushedStepHistory) {
      try {
        window.history.pushState({ authSignupStep: 2 }, '');
        this._pushedStepHistory = true;
      } catch (e) {
        // Ignore history failures
      }
    }
  };

  handleGoogleButtonClick = (redirect) => {
    if (this.state.hide && this.state.type.length < 1) {
      this.props.accountTypeError.length = 0;
      this.props.accountTypeError.push('Please select account type');
      this.setState({ showTypeError: true });
      return;
    }

    if (this.state.type.length < 1) {
      this.setState({ hide: true });
      return;
    }

    trackEvent('signup-google-attempt', { type: this.state.type });
    setLastAuth({
      email: this.state.email,
    });
    this.saveLocalUserType();
    this.saveLocalAICollectorData();
    this.props.googleOAuthRedirect(redirect);
  };

  renderRoleCards = () => {
    const { type, step, sellerEmailHold } = this.state;
    // During seller Mida hold, keep full role-step layout (no email preview / compact).
    const compact =
      step === 2 && type && !(type === 'seller' && sellerEmailHold);

    return (
      <div
        className={`${authStyles.roleStack} ${
          compact ? authStyles.roleStackCompact : ''
        }`}
        role="group"
        aria-label="How will you use Twine?"
      >
        {ROLE_OPTIONS.map((role) => {
          const selected = type === role.value;
          return (
            <button
              key={role.value}
              type="button"
              className={`${authStyles.roleCard} ${
                compact ? authStyles.roleCardCompact : ''
              } ${selected ? authStyles.roleCardSelected : ''}`}
              onClick={() => this.handleRoleSelect(role.value)}
              aria-pressed={selected}
            >
              <span className={authStyles.roleText}>
                <span className={authStyles.roleTitle}>{role.title}</span>
                <span className={authStyles.roleSubline}>{role.subline}</span>
              </span>
              <span
                className={`${authStyles.roleRadio} ${
                  selected ? authStyles.roleRadioSelected : ''
                }`}
                aria-hidden="true"
              >
                {selected ? <RoleCheckIcon /> : null}
              </span>
            </button>
          );
        })}
      </div>
    );
  };

  getBackdrop = () => {
    if (this.state.backdropVariant !== SIGNUP_BACKDROP_TEST) return null;
    return this.state.type === 'buyer' ? CLIENT_BACKDROP : FREELANCER_BACKDROP;
  };

  renderProgressive = () => {
    const {
      emailError,
      redirectFromGoogle,
      googleSignUpFailed,
      signUpPasswordlessLoading,
    } = this.props;
    const {
      step,
      type,
      email,
      apiError,
      signingUp,
      disableSubmitButton,
      sellerEmailHold,
    } = this.state;
    const showEmailStep = shouldShowSignupEmailStep({
      step,
      accountType: type,
      sellerEmailHold,
    });

    const footer = (
      <p className={authStyles.footerLine}>
        Already have an account? <a href="/signin">Sign in</a>
      </p>
    );

    if (this.state.toRender === 'loader' && !googleSignUpFailed) {
      return (
        <AuthShell>
          <Loader loading={true} />
          <div>Google login successful, redirecting...</div>
        </AuthShell>
      );
    }

    if (!(this.state.toRender === 'signup' || googleSignUpFailed)) {
      return null;
    }

    return (
      <AuthShell backdrop={this.getBackdrop()}>
        <div className={authStyles.headingBlock}>
          <h1 className={authStyles.title}>Create your account</h1>
          <p className={authStyles.subline}>Free to join. Start today.</p>
        </div>

        <p className={authStyles.roleLabel} id="signup-role-label">
          How will you use Twine?
        </p>
        {this.renderRoleCards()}

        {showEmailStep ? (
          <div className={authStyles.stepReveal}>
            <form className={authStyles.formStack} onSubmit={this.handleSubmit}>
              <div className={authStyles.field}>
                <label className={authStyles.label} htmlFor="signup-email">
                  Email
                </label>
                <input
                  id="signup-email"
                  type="email"
                  name="email"
                  autoComplete="email"
                  className={authStyles.input}
                  placeholder="name@company.com"
                  value={email}
                  maxLength={255}
                  onChange={this.handleEmailChange}
                  aria-invalid={
                    emailError && emailError.length > 0 ? true : undefined
                  }
                  aria-describedby={
                    emailError && emailError.length > 0
                      ? 'signup-email-error'
                      : undefined
                  }
                />
                {emailError && emailError.length > 0 ? (
                  <p
                    id="signup-email-error"
                    className={authStyles.errorMessage}
                    role="alert"
                  >
                    {emailError}
                  </p>
                ) : null}
              </div>

              {apiError ? (
                <p className={authStyles.errorMessage} role="alert">
                  {apiError}
                </p>
              ) : null}

              <Button
                className={authStyles.primaryButton}
                bsStyle="primary"
                type="submit"
                disabled={
                  disableSubmitButton || signingUp || signUpPasswordlessLoading
                }
              >
                {signingUp ? 'Creating account...' : 'Create free account'}
              </Button>

              <p className={authStyles.smallPrint}>
                By signing up, you agree to our <a href="/terms">Terms</a>.
              </p>

              {/* Google blocks OAuth inside WebViews, so the app is email-only. */}
              {!this.props.isNativeApp && (
                <>
                  <OrDivider />

                  <GoogleContinueButton
                    type="button"
                    onClick={() => {
                      window.onbeforeunload = null;
                      this.handleGoogleButtonClick(redirectFromGoogle);
                    }}
                  />
                </>
              )}
            </form>
          </div>
        ) : null}

        <div className={authStyles.cardFooter}>{footer}</div>

        {process.env.GOOGLE_RECAPTCHA_API_KEY && (
          <Recaptcha
            ref={(ref) => (this.recaptcha = ref)}
            sitekey={process.env.GOOGLE_RECAPTCHA_API_KEY}
            onResolved={this.handleRecaptchaResolved}
          />
        )}
      </AuthShell>
    );
  };

  renderDefault = () => {
    const { disableSubmitButton, signingUp } = this.state;
    const {
      emailError,
      redirectFromGoogle,
      accountTypeError,
      location,
      googleSignUpFailed,
    } = this.props;
    const type = location.query.type;

    const defaultAccountType = this.props.defaultAccountType
      ? this.props.defaultAccountType
      : type
      ? type
      : null;

    return (
      <Panel className={styles.signUpPanel}>
        {this.state.toRender === 'loader' && !googleSignUpFailed && (
          <>
            <Loader loading={true} />
            <div> Google login succesful, redirecting... </div>{' '}
          </>
        )}
        {(this.state.toRender === 'signup' || googleSignUpFailed) && (
          <>
            <Form horizontal style={this.state.hide ? { display: 'none' } : {}}>
              <FormGroup controlId="signup-email">
                <Col sm={3} className={styles.formLabel}>
                  <div className={styles.labelContainer}>
                    <ControlLabel>Email address:</ControlLabel>
                  </div>
                </Col>
                <Col sm={9}>
                  <EmailInput
                    type="email"
                    placeholder="e.g. name@company.com"
                    styles={styles}
                    disabled={false}
                    value={this.state.email}
                    onChange={(event) => this.handleEmailChange(event)}
                    className={styles.formField}
                    isInPost={false}
                    resetContinue={this.resetHasTriedToContinue}
                    maxLength={255}
                  />
                  {emailError && emailError.length > 0 && (
                    <HelpBlock className={styles.helpBlock}>
                      {emailError}
                    </HelpBlock>
                  )}
                </Col>
              </FormGroup>
              <FormGroup key={1} controlId="signup-usertype">
                <Col sm={3} className={styles.formLabel}>
                  <div className={styles.labelContainer}>
                    <ControlLabel>Account type:</ControlLabel>
                  </div>
                </Col>
                <Col sm={9}>
                  <AccountTypeInput
                    onChange={(event) => this.handleTypeChange(event)}
                    accountTypeError={accountTypeError}
                    defaultAccountType={defaultAccountType}
                    styles={styles}
                  />
                </Col>
              </FormGroup>
              <p className={styles.termsText}>
                By signing up, you agree to our <a href="/terms">Terms</a>.
              </p>
              {this.state.apiError && (
                <FormGroup>
                  <HelpBlock
                    className={styles.helpBlock}
                    style={{ color: 'red', textAlign: 'center' }}
                  >
                    {this.state.apiError}
                  </HelpBlock>
                </FormGroup>
              )}
              <FormGroup>
                <div className={styles.signUpContainer}>
                  <Button
                    className={`${styles.signUpButton} font-bold`}
                    bsStyle="primary"
                    bsSize="lg"
                    type="submit"
                    disabled={disableSubmitButton || signingUp}
                    onClick={(event) => this.handleSubmit(event)}
                  >
                    <div className={styles.signUpCTA}>
                      <span className={styles.signUpCopy}>
                        {signingUp ? 'Signing up...' : 'Sign up'}
                      </span>
                      {!signingUp && <SvgIcon icon="chevron-right" size="14" />}
                    </div>
                  </Button>
                </div>
              </FormGroup>
            </Form>
            {!this.props.isNativeApp && (
              <div style={this.state.hide ? { display: 'none' } : {}}>
                <Separator text="OR" />
              </div>
            )}
            <br />
            <Form horizontal style={this.state.hide ? {} : { display: 'none' }}>
              <FormGroup controlId="signup-usertype2">
                <Col sm={3} className={styles.formLabel}>
                  <ControlLabel>Account type:</ControlLabel>
                </Col>
                <Col sm={9}>
                  <AccountTypeInput
                    onChange={(event) => this.handleTypeChange(event)}
                    accountTypeError={accountTypeError}
                    styles={styles}
                  />
                </Col>
              </FormGroup>
              <br />
            </Form>
            <div>
              <GoogleButton
                googleOAuthRedirect={this.handleGoogleButtonClick}
                redirect={redirectFromGoogle}
              />
            </div>
            <br />
            <hr />
            <div style={{ textAlign: 'center' }}>
              <span>
                Already have an account? <a href="/signin">Sign in</a>
              </span>
            </div>
            {process.env.GOOGLE_RECAPTCHA_API_KEY && (
              <Recaptcha
                ref={(ref) => (this.recaptcha = ref)}
                sitekey={process.env.GOOGLE_RECAPTCHA_API_KEY}
                onResolved={this.handleRecaptchaResolved}
              />
            )}
          </>
        )}
      </Panel>
    );
  };

  render() {
    const { shouldRedirect, isCollector } = this.state;
    const googleShouldRedirect = this.retrieveLocalShouldRedirect();
    const googleIsCollector = this.retrieveLocalIsCollector();
    const { history } = this.props;
    const redirect = this.props.redirect ? this.props.redirect : '/';

    if (shouldRedirect || googleShouldRedirect) {
      const googleUserType = this.retrieveLocalUserType();
      if (isCollector || googleIsCollector) {
        history.push(`/${redirect}/microprojects?type=collector`);
      } else if (googleUserType === 'buyer') {
        history.push(`/${redirect}/client`);
      } else {
        history.push(`/${redirect}`);
      }
    }

    if (this.props.layout === 'progressive') {
      return this.renderProgressive();
    }

    return this.renderDefault();
  }
}

export default SignUp;
